import { useEffect, useRef, useState } from "react";
import { Activity, ChevronDown, ChevronUp, Map as MapIcon, List } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LeafletQuakesMap } from "@/components/LeafletQuakesMap";

// Zona de referencia (epicentro aprox. del sismo del 24 jun 2026).
const REF = { lat: 10.5, lng: -68.5 };
const START = "2026-06-24";
const MIN_MAG = 2.5;
const MAX_KM = 500;
const LIMIT = 15;

// Fuentes sísmicas (ambas públicas, con CORS, sin API key).
const USGS = `https://earthquake.usgs.gov/fdsnws/event/1/query?format=geojson&starttime=${START}&minmagnitude=${MIN_MAG}&latitude=${REF.lat}&longitude=${REF.lng}&maxradiuskm=${MAX_KM}&orderby=time&limit=${LIMIT}`;
const EMSC = `https://www.seismicportal.eu/fdsnws/event/1/query?format=json&start=${START}&minmag=${MIN_MAG}&lat=${REF.lat}&lon=${REF.lng}&maxradius=4.5&orderby=time&limit=${LIMIT}`;
const EMSC_WS = "wss://www.seismicportal.eu/standing_order/websocket";

const haversineKm = (lat, lng) => {
  const R = 6371;
  const dLat = ((lat - REF.lat) * Math.PI) / 180;
  const dLng = ((lng - REF.lng) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((REF.lat * Math.PI) / 180) * Math.cos((lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return Math.round(R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const distKm = (aLat, aLng, bLat, bLng) => {
  const R = 6371;
  const dLat = ((aLat - bLat) * Math.PI) / 180;
  const dLng = ((aLng - bLng) * Math.PI) / 180;
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((bLat * Math.PI) / 180) * Math.cos((aLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
};

// Normaliza un Feature de USGS o EMSC a una forma común.
const SRC = { usgs: "USGS", emsc: "EMSC", ws: "EMSC" };
const normalize = (f, prefix) => {
  const p = f.properties || {};
  const c = f.geometry?.coordinates || [];
  const t = typeof p.time === "number" ? p.time : Date.parse(p.time);
  const lat = c[1], lng = c[0];
  if (lat == null || lng == null || p.mag == null) return null;
  return {
    id: `${prefix}-${f.id}`,
    mag: p.mag,
    place: p.place || p.flynn_region || "Zona afectada",
    time: t,
    lat, lng,
    depth: c[2],
    src: SRC[prefix] || "USGS",
    dist: haversineKm(lat, lng),
  };
};

// Une todas las fuentes, deduplica eventos repetidos (mismo sismo reportado por
// ambas redes) por cercanía en tiempo (<90s) y espacio (<60km), ordena y limita.
const merge = (lists) => {
  const all = [].concat(...lists).filter(Boolean).sort((a, b) => b.time - a.time);
  const kept = [];
  for (const q of all) {
    const dup = kept.some(
      (k) => Math.abs(k.time - q.time) < 90000 && distKm(k.lat, k.lng, q.lat, q.lng) < 60,
    );
    if (!dup) kept.push(q);
  }
  return kept.slice(0, LIMIT);
};

const timeAgoFactory = (t) => (ms) => {
  const diff = Date.now() - ms;
  const m = Math.floor(diff / 60000);
  if (m < 1) return t("aftershocks.justNow");
  if (m < 60) return t("aftershocks.minAgo", { n: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t("aftershocks.hAgo", { n: h });
  const d = Math.floor(h / 24);
  return t("aftershocks.dAgo", { n: d });
};

// Mismo esquema que el mapa: rojo (6+), naranja (4.5-6), amarillo (2.5-4.5).
const magColor = (mag) =>
  mag >= 6 ? "bg-[#DC2626]" : mag >= 4.5 ? "bg-[#EA580C]" : "bg-[#CA8A04]";
const sevHex = (mag) => (mag >= 6 ? "#DC2626" : mag >= 4.5 ? "#EA580C" : "#CA8A04");

export function Aftershocks() {
  const { t } = useTranslation();
  const timeAgo = timeAgoFactory(t);
  const [quakes, setQuakes] = useState(null); // null = loading/unknown, [] = none, [...] = data
  const [open, setOpen] = useState(true);
  const [view, setView] = useState("map");
  const sources = useRef({ usgs: [], emsc: [], ws: [] });
  const [, setTick] = useState(0); // fuerza refresco del tiempo relativo "hace X min"

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 30 * 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let active = true;
    const recompute = () => {
      if (!active) return;
      const list = merge([sources.current.usgs, sources.current.emsc, sources.current.ws]);
      setQuakes((prev) => (list.length ? list : prev ?? []));
    };

    const fetchSource = (url, prefix, key, extract) => {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 8000);
      fetch(url, { signal: ctrl.signal })
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((data) => {
          if (!active) return;
          sources.current[key] = (extract(data) || []).map((f) => normalize(f, prefix)).filter(Boolean);
          recompute();
        })
        .catch(() => {})
        .finally(() => clearTimeout(t));
    };

    const poll = () => {
      fetchSource(USGS, "usgs", "usgs", (d) => d.features);
      fetchSource(EMSC, "emsc", "emsc", (d) => d.features);
    };
    poll();
    // Polling cada 90s (más rápido que antes) por si el WebSocket no conecta.
    const interval = setInterval(poll, 90 * 1000);

    // WebSocket EMSC: empuje en tiempo real (segundos) de nuevos sismos.
    let ws;
    try {
      ws = new WebSocket(EMSC_WS);
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          const q = normalize(msg.data, "ws");
          if (!q || q.dist > MAX_KM || q.mag < MIN_MAG || q.time < Date.parse(START)) return;
          const cur = sources.current.ws.filter((x) => x.id !== q.id);
          sources.current.ws = [q, ...cur].slice(0, 30);
          recompute();
        } catch (e) { /* mensaje no parseable: ignorar */ }
      };
    } catch (e) { /* WS no disponible: el polling cubre la actualización */ }

    return () => {
      active = false;
      clearInterval(interval);
      try { ws && ws.close(); } catch (e) { /* noop */ }
    };
  }, []);

  // Fallback silencioso: si falla o no hay datos, no se muestra nada.
  if (!quakes || quakes.length === 0) return null;

  const latest = quakes[0]; // ya viene ordenado por tiempo (más reciente primero)

  const tabCls = (isActive) =>
    `flex items-center gap-1 px-2.5 py-1 rounded-sm text-xs font-semibold ${isActive ? "bg-[#0066CC] text-white" : "bg-neutral-100 text-neutral-600"}`;

  return (
    <div className="bg-white border border-neutral-200 rounded-sm overflow-hidden" data-testid="aftershocks-widget">
      <button onClick={() => setOpen((o) => !o)} data-testid="aftershocks-toggle"
        className="w-full flex items-center justify-between px-3 py-2.5 text-left">
        <span className="flex items-center gap-2 font-semibold text-sm text-neutral-900">
          <Activity className="w-4 h-4 text-[#B91C1C]" /> {t("aftershocks.title")}
          <span className="text-xs font-normal text-neutral-500">· USGS + EMSC</span>
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-neutral-400" /> : <ChevronDown className="w-4 h-4 text-neutral-400" />}
      </button>

      {open && (
        <div className="border-t border-neutral-100">
          <div className="flex items-center gap-2 px-3 py-2 bg-red-50 border-b border-red-100" data-testid="last-quake">
            <span className="relative flex h-2.5 w-2.5 shrink-0">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
            </span>
            <span className="text-xs text-neutral-700 truncate">
              <b className="text-neutral-900">{t("aftershocks.lastReplica")}</b> {timeAgo(latest.time)} ·{" "}
              <b style={{ color: sevHex(latest.mag) }}>M{latest.mag?.toFixed(1)}</b> · {latest.place}
            </span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2">
            <button onClick={() => setView("map")} className={tabCls(view === "map")} data-testid="aftershocks-view-map">
              <MapIcon className="w-3.5 h-3.5" /> {t("aftershocks.map")}
            </button>
            <button onClick={() => setView("list")} className={tabCls(view === "list")} data-testid="aftershocks-view-list">
              <List className="w-3.5 h-3.5" /> {t("aftershocks.list")}
            </button>
          </div>

          {view === "map" ? (
            <LeafletQuakesMap quakes={quakes} timeAgo={timeAgo} />
          ) : (
            <ul className="divide-y divide-neutral-100" data-testid="aftershocks-list">
              {quakes.map((q) => (
                <li key={q.id} className="flex items-center gap-3 px-3 py-2" data-testid={`aftershock-${q.id}`}>
                  <span className={`shrink-0 w-9 h-9 rounded-sm text-white text-sm font-bold flex items-center justify-center ${magColor(q.mag)}`}>
                    {q.mag?.toFixed(1)}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-neutral-800 truncate">{q.place}</p>
                    <p className="text-xs text-neutral-500">{timeAgo(q.time)} · {t("aftershocks.distance", { km: q.dist })}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <p className="text-[10px] text-neutral-400 px-3 py-1.5 border-t border-neutral-100">
        {t("aftershocks.footer")}
      </p>
    </div>
  );
}
